import { useCallback, useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { AssinaturaService } from '@/services';
import { Assinatura } from '@/services/AssinaturaService';
import { Ciclo, Plano, PLANOS, valorCobranca } from '@/utils/planos';
import { PageHeader } from '@/components/ui/page-header';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormikInput } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SeletorPlano } from '@/components/planos/SeletorPlano';
import { Badge, IconeTile } from '@/components/ui/cores';
import { ArrowLeftRight, CalendarCheck, CreditCard, ExternalLink, Receipt, ShieldCheck } from 'lucide-react';
import { useMarca } from '@/components/providers/MarcaProvider';
import useAuth from '@/components/hooks/use.auth';
import { ErrorPage } from '../ErrorPage';

const reais = (v?: number) => (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const data = (d?: string) => d ? new Date(`${d.substring(0, 10)}T00:00:00`).toLocaleDateString('pt-BR') : '';
const hojeIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

export const statusCobranca = (s: string) =>
  s === 'PENDING' ? 'Aguardando pagamento' : s === 'RECEIVED' || s === 'CONFIRMED' || s === 'RECEIVED_IN_CASH' ? 'Pago'
    : s === 'OVERDUE' ? 'Vencida' : s === 'REFUNDED' ? 'Estornada' : s;

const COR_COBRANCA: Record<string, string> = {
  PENDING: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  RECEIVED: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  RECEIVED_IN_CASH: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  CONFIRMED: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  OVERDUE: 'bg-red-500/15 text-red-700 dark:text-red-300',
};

const SITUACOES: Record<string, { texto: string; cor: string }> = {
  TESTE: { texto: 'Teste grátis', cor: 'bg-sky-500/15 text-sky-700 dark:text-sky-300' },
  ATIVA: { texto: 'Ativa', cor: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  ATRASADA: { texto: 'Pagamento pendente', cor: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  CANCELADA: { texto: 'Cancelada', cor: 'bg-red-500/15 text-red-700 dark:text-red-300' },
};

const TESTE_DIAS = 14;

const Fato = ({ rotulo, valor }: { rotulo: string; valor: React.ReactNode }) => (
  <div>
    <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{rotulo}</dt>
    <dd className="mt-1 font-semibold">{valor}</dd>
  </div>
);

export const AssinaturaPage = () => {
  const [assinatura, setAssinatura] = useState<Assinatura>();
  const [error, setError] = useState<AxiosError>();
  const [loading, setLoading] = useState(false);
  const [plano, setPlano] = useState<Plano>('CORRETORA');
  const [ciclo, setCiclo] = useState<Ciclo>('MENSAL');
  const [cancelando, setCancelando] = useState(false);
  const [trocando, setTrocando] = useState(false);
  const { recarregar, diasTeste } = useMarca();
  const auth = useAuth();

  const carregar = useCallback(() =>
    AssinaturaService.consultar().then(({ data }) => {
      setAssinatura(data);
      if (data.plano) setPlano(data.plano);
      if (data.ciclo) setCiclo(data.ciclo);
    }).catch(setError), []);

  useEffect(() => { carregar(); }, [carregar]);

  // Erros das escritas já aparecem no aviso global (App.tsx)
  const executar = (acao: () => Promise<any>, sucesso?: string) => {
    setLoading(true);
    return acao().then(() => { if (sucesso) toast.success(sucesso); recarregar(); return carregar(); })
      .catch(() => {}).finally(() => setLoading(false));
  };

  const ativar = (v: { cpfCnpj: string; nome: string; email: string }) =>
    executar(async () => {
      const { data: r } = await AssinaturaService.ativar({ ...v, plano, ciclo });
      // window.open depois do await pode ser bloqueado (e com noopener sempre retorna null): o aviso vale sempre
      if (r.invoiceUrl) window.open(r.invoiceUrl, '_blank', 'noopener');
      toast.success('Assinatura ativada. Se a fatura não abriu, use "Abrir fatura" na lista de cobranças.');
    });

  if (error) return <ErrorPage axiosError={error} />;
  if (!assinatura) return null;
  if (!assinatura.situacao || assinatura.situacao === 'ISENTA') return <ErrorPage statusCode={403} />;   // isenta não usa assinatura pelo site

  const a = assinatura;
  const situacao = SITUACOES[a.situacao] ?? { texto: a.situacao, cor: 'bg-muted text-muted-foreground' };
  // Cancelando, o acesso vai até a maior data entre o pago e o fim do teste (datas ISO comparam como texto)
  const acessoAte = [a.pagoAte, a.testeAte].filter(Boolean).sort().pop();
  // Mesma regra da API: a 1ª cobrança vence no fim do teste (ou hoje, se o teste já acabou ou o pago vai além)
  const primeiraCobranca = [hojeIso(), a.testeAte, a.pagoAte].filter(Boolean).sort().pop();
  const proxima = a.cobrancas.filter(c => c.status === 'PENDING' || c.status === 'OVERDUE').sort((x, y) => x.dueDate.localeCompare(y.dueDate))[0];
  const emTeste = a.situacao === 'TESTE' && diasTeste !== undefined;
  const mudouPlano = plano !== a.plano || ciclo !== a.ciclo;

  return (
    <div className="space-y-6">
      <PageHeader icon={<IconeTile cor="bg-sky-500/10 text-sky-600 dark:text-sky-400"><CreditCard /></IconeTile>} title="Assinatura" />

      {/* Resumo do plano */}
      <Card className="p-6 shadow-none">
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Plano contratado</p>
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <h2 className="text-2xl font-bold tracking-tight">{a.plano ? PLANOS[a.plano].nome : 'Sem plano'}</h2>
              <Badge className={situacao.cor}>{situacao.texto}</Badge>
            </div>
            {a.plano && (
              <p className="mt-1 text-muted-foreground">
                <span className="text-lg font-semibold text-foreground">{reais(a.valor)}</span> {a.ciclo === 'ANUAL' ? 'por ano' : 'por mês'}
                {' · '}{PLANOS[a.plano].usuarios === 1 ? '1 usuário' : `até ${PLANOS[a.plano].usuarios} usuários`}
              </p>
            )}
            {a.ativa && (
              <Button variant="outline" size="sm" className="mt-4" disabled={loading}
                onClick={() => { if (a.plano) setPlano(a.plano); if (a.ciclo) setCiclo(a.ciclo); setTrocando(true); }}>
                <ArrowLeftRight aria-hidden className="h-4 w-4" /> Mudar de plano
              </Button>
            )}
          </div>
          <dl className="grid grid-cols-2 gap-x-10 gap-y-4 sm:grid-cols-3 md:text-right">
            {a.situacao === 'TESTE' && <Fato rotulo="Teste grátis até" valor={data(a.testeAte)} />}
            {a.pagoAte && <Fato rotulo={a.situacao === 'CANCELADA' ? 'Acesso até' : 'Pago até'} valor={data(a.pagoAte)} />}
            {proxima && <Fato rotulo="Próxima cobrança" valor={`${data(proxima.dueDate)} · ${reais(proxima.value)}`} />}
            <Fato rotulo="Cobrança" valor={a.ativa ? 'Automática' : 'Não ativada'} />
          </dl>
        </div>
        {emTeste && (
          <div className="mt-6">
            <div className="mb-2 flex justify-between text-sm">
              <span className="font-medium">{diasTeste === 0 ? 'Último dia de teste' : `Faltam ${diasTeste} dia${diasTeste === 1 ? '' : 's'} de teste`}</span>
              <span className="text-muted-foreground">{TESTE_DIAS - Math.min(TESTE_DIAS, diasTeste!)} de {TESTE_DIAS} dias</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-[#2BB673]" style={{ width: `${Math.max(4, ((TESTE_DIAS - Math.min(TESTE_DIAS, diasTeste!)) / TESTE_DIAS) * 100)}%` }} />
            </div>
          </div>
        )}
      </Card>

      {!a.ativa && (
        <Formik initialValues={{ cpfCnpj: '', nome: '', email: auth.user?.email || '' }} enableReinitialize onSubmit={ativar}
          validationSchema={Yup.object({
            cpfCnpj: Yup.string().required('Informe o CPF ou CNPJ')
              .test('documento', 'CPF ou CNPJ inválido', v => [11, 14].includes((v || '').replace(/\D/g, '').length)),
            nome: Yup.string().trim().required('Informe o nome ou a razão social'),
            email: Yup.string().trim().email('E-mail inválido').required('Informe o e-mail de cobrança'),
          })}>
          <Form className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
            <Card className="space-y-8 p-6 shadow-none">
              <div>
                <h2 className="text-lg font-semibold">Ativar assinatura</h2>
                <p className="text-sm text-muted-foreground">Escolha o plano e informe os dados para a cobrança e a nota fiscal.</p>
              </div>
              <SeletorPlano plano={plano} ciclo={ciclo} onChange={(p, c) => { setPlano(p); setCiclo(c); }} titulo="1. Plano" />
              <div className="space-y-4">
                <p className="text-sm font-semibold">2. Dados de cobrança</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><FormikInput name="cpfCnpj" label="CPF ou CNPJ" inputMode="numeric" maxLength={18} placeholder="00.000.000/0000-00" /></div>
                  <div><FormikInput name="nome" label="Nome ou razão social" placeholder="Como deve sair na nota fiscal" /></div>
                </div>
                <div><FormikInput name="email" label="E-mail de cobrança" type="email" placeholder="financeiro@corretora.com.br" /></div>
              </div>
            </Card>

            <Card className="h-fit space-y-5 p-6 shadow-none lg:sticky lg:top-20">
              <h3 className="font-semibold">Resumo</h3>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Plano</dt><dd className="font-medium">{PLANOS[plano].nome} · {ciclo === 'ANUAL' ? 'anual' : 'mensal'}</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Usuários</dt><dd className="font-medium">{PLANOS[plano].usuarios === 1 ? '1' : `até ${PLANOS[plano].usuarios}`}</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-muted-foreground">1ª cobrança</dt><dd className="font-medium">{data(primeiraCobranca)}</dd></div>
              </dl>
              <div className="flex items-baseline justify-between border-t pt-4">
                <span className="text-sm text-muted-foreground">{ciclo === 'ANUAL' ? 'Total por ano' : 'Total por mês'}</span>
                <span className="text-2xl font-bold">{reais(valorCobranca(plano, ciclo))}</span>
              </div>
              <Button type="submit" disabled={loading} className="h-11 w-full rounded-full bg-[#2BB673] text-base font-semibold text-white hover:bg-[#24a465]">
                {loading ? 'Ativando...' : 'Ativar e ir para o pagamento'}
              </Button>
              <ul className="space-y-2 text-xs text-muted-foreground">
                <li className="flex items-center gap-2"><ShieldCheck aria-hidden className="h-4 w-4 shrink-0" /> Pagamento seguro pelo Asaas: Pix, boleto ou cartão</li>
                <li className="flex items-center gap-2"><Receipt aria-hidden className="h-4 w-4 shrink-0" /> Nota fiscal enviada por e-mail</li>
                <li className="flex items-center gap-2"><CalendarCheck aria-hidden className="h-4 w-4 shrink-0" /> Sem fidelidade: cancele quando quiser</li>
              </ul>
            </Card>
          </Form>
        </Formik>
      )}

      {/* Cobranças */}
      <Card className="p-6 shadow-none">
        <h2 className="mb-4 text-lg font-semibold">Cobranças</h2>
        {a.cobrancas.length === 0 ? (
          <div className="flex flex-col items-center py-8 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted"><Receipt className="h-6 w-6 text-muted-foreground" /></span>
            <p className="mt-3 font-medium">Nenhuma cobrança ainda</p>
            <p className="text-sm text-muted-foreground">As faturas aparecem aqui depois que a assinatura for ativada.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Vencimento</TableHead><TableHead>Valor</TableHead><TableHead>Situação</TableHead><TableHead className="text-right">Fatura</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {a.cobrancas.map(c => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">{data(c.dueDate)}</TableCell>
                  <TableCell>{reais(c.value)}</TableCell>
                  <TableCell><Badge className={COR_COBRANCA[c.status] ?? ''}>{statusCobranca(c.status)}</Badge></TableCell>
                  <TableCell className="text-right">
                    {c.invoiceUrl && (
                      <a className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline" href={c.invoiceUrl} target="_blank" rel="noopener noreferrer">
                        Abrir fatura <ExternalLink aria-hidden className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {a.ativa && (
        <Card className="flex flex-col gap-4 border-destructive/30 p-6 shadow-none sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-semibold">Cancelar assinatura</h2>
            <p className="text-sm text-muted-foreground">As cobranças param. Você continua com acesso até o fim do período pago e depois só consulta.</p>
          </div>
          <Button variant="outline" className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive" disabled={loading} onClick={() => setCancelando(true)}>
            Cancelar assinatura
          </Button>
        </Card>
      )}

      <Dialog open={trocando} onOpenChange={setTrocando}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mudar de plano</DialogTitle>
            <DialogDescription>O novo valor vale a partir da próxima cobrança.</DialogDescription>
          </DialogHeader>
          <SeletorPlano plano={plano} ciclo={ciclo} onChange={(p, c) => { setPlano(p); setCiclo(c); }} titulo="Planos" atual={{ plano: a.plano, ciclo: a.ciclo }} />
          <p className="text-sm text-muted-foreground">
            {mudouPlano ? <>Novo valor: <strong className="text-foreground">{reais(valorCobranca(plano, ciclo))}</strong> {ciclo === 'ANUAL' ? 'por ano' : 'por mês'}</> : 'Selecione outro plano ou ciclo para trocar.'}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTrocando(false)}>Voltar</Button>
            <Button disabled={loading || !mudouPlano}
              onClick={() => executar(() => AssinaturaService.trocarPlano(plano, ciclo), 'Plano alterado. O novo valor vale a partir da próxima cobrança.').then(() => setTrocando(false))}>
              Continuar com {PLANOS[plano].nome} {ciclo === 'ANUAL' ? 'anual' : 'mensal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelando} onOpenChange={setCancelando}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar assinatura?</DialogTitle>
            <DialogDescription>
              {acessoAte ? `Você continua com acesso até ${data(acessoAte)}. Depois disso, só consulta.` : 'Depois de cancelar, a corretora passa a só consultar.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelando(false)}>Manter assinatura</Button>
            <Button variant="destructive" disabled={loading}
              onClick={() => executar(() => AssinaturaService.cancelar(), 'Assinatura cancelada.').then(() => setCancelando(false))}>
              Cancelar assinatura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
